"use client"

import { EVENT_SUBSCRIBERS } from "@/constants/admin-paths"
import { useGuardedForm } from "@/hook/useGuardedForm"
import { getEvents } from "@/services/event-services"
import { EventSubscriberFieldType } from "@/types/types"
import { createPath } from "@/utils/create-path"
import { normalizeFormData } from "@/utils/normalize-form-data"
import { Form } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useState } from "react"
import { useEventSubscribers } from "./useEventSubscriber"

export const useUpsertEventSubscribers = () => {
  const [form] = Form.useForm()
  const router = useRouter()
  const searchParams = useSearchParams()
  const id = searchParams.get("id")
  const isEditMode = !!id

  const [isFormChanged, setIsFormChanged] = useState(false)
  const [initialValues, setInitialValues] = useState<EventSubscriberFieldType | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [eventOptions, setEventOptions] = useState<{ label: string; value: number }[]>([])

  const { loading, actions } = useEventSubscribers()
  const { fetchEventSubscriberByIdAndSetForm, handleUpsertEventSubscriber } = actions
  const [isFormReady, setIsFormReady] = useState(false);
  const getNormalizedSnapshot = useCallback(() => {
    return normalizeFormData(form.getFieldsValue(true));
  }, [form])


  const { isChange, checkChange, message, modal, commitSnapshot, handleCancel: guardCancel } = useGuardedForm({
    form,
    getSnapshot: getNormalizedSnapshot
  });

  const fetchEventOptions = useCallback(async () => {
    try {
      const response = await getEvents()
      if (response && response.data) {
        const options = response.data.map(ev => ({
          label: ev.attributes.name,
          value: ev.id,
        }))
        setEventOptions(options)
      }
    } catch (error) {
      console.error("Failed to fetch events for select", error)
    }
  }, [])

  const loadData = useCallback(async () => {
    if (isEditMode && id) {
      const { formData } = await fetchEventSubscriberByIdAndSetForm(Number(id), form)
      if (formData) {
        const eventField = formData.event
        let eventId: string | number | undefined

        if (eventField && typeof eventField === "object" && "data" in eventField) {
          eventId = eventField.data?.id
        } else {
          eventId = eventField as string | number
        }

        const patchValue = {
          ...formData,
          event: eventId,
        }

        form.setFieldsValue(patchValue)
        setIsFormReady(true);
      }
    }
  }, [id, isEditMode, fetchEventSubscriberByIdAndSetForm, form])

  useEffect(() => {
    fetchEventOptions()
    if (isEditMode) loadData()
  }, [loadData, isEditMode, fetchEventOptions])

  useEffect(() => {
    if (!isEditMode) {
      setIsFormReady(true);
    }
  }, [isEditMode]);

  useEffect(() => {
    if (isFormReady) {
      const animationId = requestAnimationFrame(() => {
        commitSnapshot();
      });
      return () => cancelAnimationFrame(animationId);
    }
  }, [isFormReady, commitSnapshot]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true)
      const values = await form.validateFields()

      const payload = {
        ...values,
        event: values.event,
      }

      const result = await handleUpsertEventSubscriber(payload, id ? Number(id) : undefined)
      if (result.success) {
        if (isEditMode) {
          commitSnapshot();
        } else {
          router.push(createPath(EVENT_SUBSCRIBERS.LIST.PATH, searchParams))
        }
      }
    } finally {
      setSubmitting(false)
    }
  }

  return {
    form,
    isEditMode,
    loading,
    submitting,
    eventOptions,
    isChange,
    isButtonDisabled: isEditMode ? !isFormChanged : false,
    handlers: {
      handleSubmit,
      handleBack: () => {
        const fallbackUrl = createPath(EVENT_SUBSCRIBERS.LIST.PATH, searchParams);
        guardCancel(fallbackUrl);
      },
      handleFormChange: checkChange
    },
  }
}